import type { RefObject } from 'react';
import { useCallback, useEffect, useEffectEvent, useRef } from 'react';

import { isInsideFloatingOverlay } from '@/lib/base/overlays/floating-overlay';

export interface UseInlineMemoAutosaveOptions {
  /** Whether the inline (record-slideout) autosave behavior is active. */
  enabled: boolean;
  /** Whether there's an unsaved change worth persisting. */
  isDirty: boolean;
  /** Persist the current value. Must itself no-op when there's nothing to save. */
  commit: () => void;
  /** Return to the read view after a commit (or on unmount without one). */
  onExit: () => void;
  /**
   * The form's own container — clicks inside it never trigger a commit, and
   * it's the `surface` passed to `isInsideFloatingOverlay` so a control's own
   * portaled popover (date / owner / privacy / stage pickers) is recognized
   * as part of editing, not an "outside" click.
   */
  containerRef: RefObject<HTMLElement | null>;
}

export interface UseInlineMemoAutosaveResult {
  /** Commit the current value and exit — wired to Esc / ⌘+Enter / click-away. */
  commitAndExit: () => void;
}

/**
 * Debounced autosave + commit-on-exit for MemoForm's inline (record-slideout)
 * variant, which has no Save/Cancel: edits persist automatically, and every
 * way out — Esc, a click elsewhere, ⌘+Enter, or the editor unmounting —
 * flushes the latest edit first.
 *
 * Click-away discriminates a click on a control's own portaled popover from a
 * genuine "elsewhere" click via `isInsideFloatingOverlay` — never re-derive
 * that locally. An earlier local role-based check matched the page's contacts
 * grid and swallowed clicks meant to save-and-exit; a later local
 * `[data-placement]` check drifted from another surface's own copy of the
 * same idea. See lib/base/overlays/floating-overlay.
 */
export function useInlineMemoAutosave({
  enabled,
  isDirty,
  commit,
  onExit,
  containerRef,
}: UseInlineMemoAutosaveOptions): UseInlineMemoAutosaveResult {
  // Tracks whether the CURRENT dirty edit has already been persisted — by an
  // explicit commit (Esc / click-away / ⌘+Enter) OR by the debounce firing.
  // The unmount flush reads it to avoid submitting the same edit twice. It is
  // re-armed (cleared) below whenever a fresh edit arrives, so a new revision
  // can still flush on unmount.
  const committedRef = useRef(false);

  const commitAndExit = useCallback(() => {
    committedRef.current = true;
    commit();
    onExit();
  }, [commit, onExit]);

  // Debounced autosave. The timer resets whenever `isDirty` or `commit`'s
  // identity changes, so the save fires ~800ms after the user pauses.
  // `isDirty` going false (the caller's own optimistic update landing) stops
  // it re-firing.
  //
  // Re-arm on every fresh dirty edit: entering the effect body with a dirty
  // value means the user has an unpersisted change, which is a NEW revision
  // relative to whatever the last commit saved — so clear `committedRef` and
  // let the unmount flush persist it if the debounce hasn't yet. When the
  // debounce DOES fire, it records the commit (`committedRef.current = true`)
  // so an unmount in the window before the optimistic update lands (while
  // `isDirty` is still true) does not re-submit the SAME edit.
  useEffect(() => {
    if (!enabled || !isDirty) {
      return;
    }
    committedRef.current = false;
    const timer = setTimeout(() => {
      committedRef.current = true;
      commit();
    }, 800);
    return () => clearTimeout(timer);
  }, [enabled, isDirty, commit]);

  // Flush a pending edit if the inline editor unmounts WITHOUT the current
  // edit already persisted — e.g. the user closes the slideout, toggles edit
  // off from the header, or navigates to a linked record mid-edit, before the
  // 800ms debounce fires. Those exits happen via interactive controls that the
  // click-away handler deliberately leaves alone (so their own click isn't
  // swallowed), so without this a sub-800ms edit could be lost. `committedRef`
  // skips the flush whenever the current edit was ALREADY persisted — by the
  // normal Esc / click-away / ⌘+Enter path OR by the debounce firing — so the
  // same edit is never submitted twice (the debounce sets it before the
  // caller's optimistic update flips `isDirty` false). `commit` is itself a
  // no-op when nothing is dirty. An Effect Event so the cleanup always calls
  // the LATEST `commit` without re-subscribing the effect every render.
  const flushOnUnmount = useEffectEvent(() => {
    if (!committedRef.current) {
      commit();
    }
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return () => {
      flushOnUnmount();
    };
    // flushOnUnmount is an Effect Event — never a dependency (REACT_19_PATTERNS § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Click-away to exit. A capture-phase pointerdown listener: clicks inside
  // the form are ignored, as are clicks inside a portaled control popover, so
  // interacting with a control never kicks the user out mid-pick. Any OTHER
  // click — elsewhere in the slideout OR out in the main page area — commits
  // and exits.
  const handlePointerDownOutside = useEffectEvent((e: PointerEvent) => {
    const target = e.target as HTMLElement | null;
    const form = containerRef.current;
    if (!target || !form) {
      return;
    }
    if (form.contains(target)) {
      return;
    }
    if (isInsideFloatingOverlay(target, { surface: form })) {
      return;
    }
    // Don't hijack a pointerdown that lands on an interactive control
    // elsewhere in the slideout or page — e.g. the linked-records trash /
    // "+ Link record" button, or an entity row that navigates. Committing here
    // fires onExit(), which collapses the taller edit view ON pointerdown and
    // shifts the control out from under the release, so its click never lands
    // (the trash "couldn't be clicked"). Let the control run its own handler;
    // the note still autosaves (debounced), and a pointerdown on
    // non-interactive dead space still commits + exits.
    const interactive = target.closest(
      'a, button, input, select, textarea, [role="button"], [role="menuitem"], [role="option"], [contenteditable="true"]'
    );
    if (interactive) {
      return;
    }
    commitAndExit();
  });

  // Esc commits + exits — a document-level fallback so it fires even when
  // focus isn't on the form's own inputs (after caret-at-click the Plate
  // editor holds focus and can swallow Esc, and focus can also rest on
  // <body>). If a control popover is open, react-aria owns Esc (closes the
  // popover) — detect that (any overlay other than the form itself) and skip,
  // so Esc there never exits the editor. stopPropagation keeps the same Esc
  // from also closing an enclosing slideout.
  const handleEscape = useEffectEvent((e: KeyboardEvent) => {
    if (e.key !== 'Escape') {
      return;
    }
    if (
      isInsideFloatingOverlay(document.activeElement, {
        surface: containerRef.current,
      })
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    commitAndExit();
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onPointerDown = (e: PointerEvent) => handlePointerDownOutside(e);
    const onKeyDown = (e: KeyboardEvent) => handleEscape(e);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
    // handlePointerDownOutside/handleEscape are Effect Events — never
    // dependencies (REACT_19_PATTERNS § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { commitAndExit };
}
